/**
 * Thin compatibility layer over TanStack Router.
 *
 * The app was written against `next/navigation` + `next/link`. After the Vite
 * SPA migration these wrappers keep the call sites essentially unchanged — only
 * the import path moves from `next/*` to `@/lib/router-compat`. Tests mock this
 * single module instead of `next/navigation`.
 */
import { useCallback } from 'react'
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react'
import { useNavigate, useParams as useRouterParams, useSearch } from '@tanstack/react-router'

// TanStack's navigate is strongly typed against the generated route tree. These
// wrappers accept plain href strings (the next/navigation contract), so we
// translate to navigate options and relax the type at this single boundary.
type NavigateFn = ReturnType<typeof useNavigate>

function toNavOptions(href: string, replace = false): Parameters<NavigateFn>[0] {
  const url = new URL(href, window.location.origin)
  return {
    to: url.pathname,
    search: Object.fromEntries(url.searchParams.entries()),
    hash: url.hash ? url.hash.slice(1) : undefined,
    replace,
  } as unknown as Parameters<NavigateFn>[0]
}

/** Subset of next/navigation's `useRouter()` the app relied on. */
export function useRouter() {
  const navigate = useNavigate()
  return {
    push: (href: string) => navigate(toNavOptions(href)),
    replace: (href: string) => navigate(toNavOptions(href, true)),
  }
}

/** Read-only view of the current search params, exposing the `.get()` the app used. */
export function useSearchParams() {
  const search = useSearch({ strict: false }) as Record<string, unknown>
  return {
    get(key: string): string | null {
      const value = search[key]
      return value == null ? null : String(value)
    },
  }
}

/** Current route params, e.g. `{ token }` on `/shared/$token`. */
export function useParams<T extends Record<string, string>>(): T {
  return useRouterParams({ strict: false } as never) as T
}

function isExternal(href: string): boolean {
  return /^([a-z]+:)?\/\//i.test(href) || /^(mailto:|tel:|#)/i.test(href)
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  href: string
  children?: ReactNode
}

/** Drop-in for `next/link`: anchor with client-side navigation on plain clicks. */
export function Link({ href, onClick, children, ...rest }: LinkProps) {
  const navigate = useNavigate()
  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event)
      if (event.defaultPrevented) return
      // Let the browser handle modifier/middle clicks, new tabs, and externals.
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (rest.target && rest.target !== '_self') ||
        isExternal(href)
      ) {
        return
      }
      event.preventDefault()
      navigate(toNavOptions(href))
    },
    [href, navigate, onClick, rest.target]
  )

  return (
    <a href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  )
}
